/**
 * Centralized Application Configuration
 *
 * This file consolidates all environment variables and configuration settings
 * to provide a single source of truth for the application.
 * All hard-coded values and environment-specific settings are managed here.
 */

const middlewareBaseUrl = (
  process.env.REACT_APP_MIDDLEWARE_API ||
  "https://7t7lyhcrhmyhv4pz6ezcotm34m0zopmf.lambda-url.ap-southeast-1.on.aws"
).replace(/\/$/, "");

const appConfig = {
  // Environment
  nodeEnv: process.env.NODE_ENV || "development",
  port: 3003,

  // Application URLs
  app: {
    mainUrl: process.env.REACT_APP_MAINURL || "http://localhost:3003",
    publicUrl: process.env.PUBLIC_URL || "http://localhost:3003",
    subDir: process.env.REACT_APP_SUBDIR || "/",
  },

  // API Configuration
  api: {
    epaymentBaseUrl: `${middlewareBaseUrl}/api/epayment`,
    opListBaseUrl: `${middlewareBaseUrl}/api/epayment/op-list`,
    verifyUserId: `${middlewareBaseUrl}/api/verify`,
    siteStatus: `${middlewareBaseUrl}/api/admin/settings`,
  },

  // External Integration URLs
  external: {
    redirectUrls: {
      default: process.env.REACT_APP_DEFAULT_REDIRECT_URL || "",
      withBillingNumber:
        process.env.REACT_APP_BILLING_NUMBER_REDIRECT_URL || "",
    },
  },

  // Feature Flags
  features: {
    enableBackButton: true,
    enableQRCode: true,
    enablePDFDownload: true,
  },

  // Payment Status Mapping
  paymentStatus: {
    I: "PENDING",
    C: "CANCELLED",
    F: "FAILED",
    E: "EXPIRED",
    S: "SUCCESSFUL",
  },

  // UI Configuration
  ui: {
    pdfExportEnabled: true,
    qrCodeSize: 192,
    qrCodeLevel: "H",
  },
};

// Validation helper to ensure required config is present
export const validateConfig = () => {
  const errors = [];

  if (!appConfig.api.epaymentBaseUrl) {
    errors.push("REACT_APP_EPAYMENT_API is required");
  }

  if (!appConfig.api.opListBaseUrl) {
    errors.push("REACT_APP_EPAYMENT_OP_API is required");
  }

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error("Configuration Errors:", errors);
    if (process.env.NODE_ENV === "production") {
      throw new Error(`Missing required configuration: ${errors.join(", ")}`);
    }
  }

  return errors.length === 0;
};

// Helper to get redirect URL based on context
export const getRedirectUrl = (qcUserId, hasBillingNumber = false) => {
  const baseUrl = hasBillingNumber
    ? appConfig.external.redirectUrls.withBillingNumber
    : appConfig.external.redirectUrls.default;

  return baseUrl ? `${baseUrl}/${qcUserId}` : null;
};

// Helper to get payment status text
export const getPaymentStatusText = (statusCode) =>
  appConfig.paymentStatus[statusCode] || "N/A";

export const formatPhpAmount = (amount) => {
  if (amount === null || amount === undefined) {
    return "N/A";
  }

  const stripped = String(amount).replace(/,/g, "").trim();

  if (stripped === "") {
    return "N/A";
  }

  const numericAmount = Number(stripped);

  if (!Number.isFinite(numericAmount)) {
    return "N/A";
  }

  return `₱ ${new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount)}`;
};

export default appConfig;
